import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import RouteGuard from './components/RouteGuard';
import AnalyticsConsentBanner from './components/AnalyticsConsentBanner';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const VisionFlowRegister = lazy(() => import('./pages/auth/VisionFlowRegister'));
const EmailVerification = lazy(() => import('./pages/auth/EmailVerification'));
const VisionFlowWelcome = lazy(() => import('./pages/onboarding/VisionFlowWelcome'));
const VisionFlowWizard = lazy(() => import('./pages/onboarding/VisionFlowWizard'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Patient and Appointment pages
const Patients = lazy(() => import('./pages/Patients'));
const Appointments = lazy(() => import('./pages/Appointments'));
const ImportWizard = lazy(() => import('./pages/imports/ImportWizard'));

// Orders, payments and GST invoices
const OrdersList = lazy(() => import('./pages/orders/OrdersList'));
const NewOrder = lazy(() => import('./pages/orders/NewOrder'));
const OrderDetail = lazy(() => import('./pages/orders/OrderDetail'));
const InvoiceView = lazy(() => import('./pages/orders/InvoiceView'));

// Analytics pages
const AnalyticsDashboard = lazy(() => import('./pages/analytics/Dashboard'));
const PatientDemographics = lazy(() => import('./pages/analytics/PatientDemographics'));
const RevenueAnalytics = lazy(() => import('./pages/analytics/RevenueAnalytics'));
const PracticeOptimization = lazy(() => import('./pages/analytics/PracticeOptimization'));
const AppointmentOptimization = lazy(() => import('./pages/analytics/AppointmentOptimization'));
const ContactLensAnalytics = lazy(() => import('./pages/analytics/ContactLensAnalytics'));
const LocationAnalytics = lazy(() => import('./pages/analytics/LocationAnalytics'));

// Marketing pages
const MarketingDashboard = lazy(() => import('./pages/marketing/Dashboard'));
const Campaigns = lazy(() => import('./pages/marketing/campaigns/Campaigns'));
const NewCampaign = lazy(() => import('./pages/marketing/campaigns/NewCampaign'));
const Segments = lazy(() => import('./pages/marketing/segments/Segments'));
const Templates = lazy(() => import('./pages/marketing/templates/Templates'));

// Inventory
const Inventory = lazy(() => import('./pages/inventory/Inventory'));

// Loyalty program pages
const LoyaltyDashboard = lazy(() => import('./pages/loyalty/Dashboard'));
const LoyaltyRewards = lazy(() => import('./pages/loyalty/Rewards'));
const LoyaltyMembers = lazy(() => import('./pages/loyalty/Members'));
const LoyaltyTiers = lazy(() => import('./pages/loyalty/Tiers'));
const LoyaltyTransactions = lazy(() => import('./pages/loyalty/Transactions'));

// Support and demo pages
const FAQ = lazy(() => import('./pages/support/FAQ'));
const PMSPlatforms = lazy(() => import('./pages/support/PMSPlatforms'));
const DemoData = lazy(() => import('./pages/demo/DemoData'));
const DemoAccess = lazy(() => import('./pages/internal/DemoAccess'));

// IRIS AI Assistant
const IrisDashboard = lazy(() => import('./pages/IrisEnhanced'));

// Knowledge Base
const KnowledgeBase = lazy(() => import('./pages/knowledge/KnowledgeBase'));
const NoteEditor = lazy(() => import('./pages/knowledge/NoteEditor'));
const NoteViewer = lazy(() => import('./pages/knowledge/NoteViewer'));
const GraphView = lazy(() => import('./pages/knowledge/GraphView'));

// Marketing pages
const Homepage = lazy(() => import('./pages/marketing/Homepage'));

// Public pages for third-party evaluation
const SecurityTerms = lazy(() => import('./pages/public/SecurityTerms'));
const TermsOfService = lazy(() => import('./pages/public/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/public/PrivacyPolicy'));
const DataProcessingAgreement = lazy(() => import('./pages/public/DataProcessingAgreement'));

// Onboarding pages
const OnboardingWizard = lazy(() => import('./pages/onboarding/Wizard'));

// Corporate admin pages
const CorporateLogin = lazy(() => import('./pages/corporate/LoginPage'));
const CorporateDashboard = lazy(() => import('./pages/corporate/DashboardPage'));

// Pricing page (public)
const Pricing = lazy(() => import('./pages/Pricing'));

// Settings page
const Settings = lazy(() => import('./pages/Settings'));

// 403 Forbidden (RouteGuard target)
const Forbidden = lazy(() => import('./pages/Forbidden'));

// Chain hierarchy list pages (PRD-0002 §4.2 / B1)
const CorporationList = lazy(() => import('./pages/chain/CorporationList'));
const RegionsList = lazy(() => import('./pages/chain/RegionsList'));
const DistrictsList = lazy(() => import('./pages/chain/DistrictsList'));
const StoresList = lazy(() => import('./pages/chain/StoresList'));
const PracticesList = lazy(() => import('./pages/chain/PracticesList'));

// Chain hierarchy detail pages (PRD-0002 §4.2 / B2)
const CorporationDetail = lazy(() => import('./pages/chain/CorporationDetail'));
const RegionDetail = lazy(() => import('./pages/chain/RegionDetail'));
const DistrictDetail = lazy(() => import('./pages/chain/DistrictDetail'));
const StoreDetail = lazy(() => import('./pages/chain/StoreDetail'));
const PracticeDetail = lazy(() => import('./pages/chain/PracticeDetail'));

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AnalyticsConsentBanner />
        <Suspense fallback={<div className="flex h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>}>
          <Routes>
          {/* Marketing homepage at root */}
          <Route path="/" element={<Homepage />} />

          {/* Demo route */}
          <Route path="/demo" element={<DemoData />} />

          {/* Internal demo credentials — hidden, not linked publicly */}
          <Route path="/internal/demo" element={<DemoAccess />} />

          {/* PMS Platforms public page */}
          <Route path="/pms-platforms" element={<PMSPlatforms />} />

          {/* Pricing (public); checkout and billing are unrouted, see /app routes below */}
          <Route path="/pricing" element={<Pricing />} />

          {/* Public security and compliance documentation */}
          <Route path="/security-terms" element={<SecurityTerms />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/data-processing-agreement" element={<DataProcessingAgreement />} />

          {/* Direct Marketing Routes (accessible without /app prefix) */}
          <Route path="/marketing" element={<Layout />}>
            <Route index element={<MarketingDashboard />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/new" element={<NewCampaign />} />
            <Route path="campaigns/:id" element={<NewCampaign />} />
            <Route path="campaigns/:id/edit" element={<NewCampaign />} />
            <Route path="segments" element={<Segments />} />
            <Route path="segments/new" element={<Segments />} />
            <Route path="segments/:id" element={<Segments />} />
            <Route path="segments/:id/edit" element={<Segments />} />
            <Route path="templates" element={<Templates />} />
            <Route path="templates/new" element={<Templates />} />
            <Route path="templates/:id" element={<Templates />} />
            <Route path="templates/:id/edit" element={<Templates />} />
          </Route>

          {/* Practice User Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* VisionFlow Registration & Onboarding Routes */}
          <Route path="/visionflow/register" element={<VisionFlowRegister />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/onboarding/welcome" element={<VisionFlowWelcome />} />
          <Route path="/onboarding/visionflow/*" element={<VisionFlowWizard />} />

          {/* Direct access redirects to app routes */}
          <Route path="/patients" element={<Navigate to="/app/patients" replace />} />
          <Route path="/appointments" element={<Navigate to="/app/appointments" replace />} />
          <Route path="/analytics" element={<Navigate to="/app/analytics" replace />} />
          <Route path="/app" element={<Layout />}>
            <Route index element={<RouteGuard feature="dashboard"><Dashboard /></RouteGuard>} />
            {/* 403 page — accessible to any authenticated user */}
            <Route path="forbidden" element={<Forbidden />} />
            {/* Legacy /app/stores → /app/chain/stores redirect.
                Sidebar link is already correct; this catches the
                bookmarked / hand-typed path that previously 404'd. */}
            <Route path="stores" element={<Navigate to="/app/chain/stores" replace />} />
            <Route path="analytics" element={<RouteGuard feature="analytics"><Outlet /></RouteGuard>}>
              <Route index element={<AnalyticsDashboard />} />
              <Route path="demographics" element={<PatientDemographics />} />
              <Route path="revenue" element={<RevenueAnalytics />} />
              <Route path="practice-optimization" element={<PracticeOptimization />} />
              <Route path="appointment-optimization" element={<AppointmentOptimization />} />
              <Route path="contact-lens" element={<ContactLensAnalytics />} />
              <Route path="location" element={<LocationAnalytics />} />
            </Route>
            <Route path="marketing" element={<RouteGuard feature="marketing"><Outlet /></RouteGuard>}>
              <Route index element={<MarketingDashboard />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="campaigns/new" element={<NewCampaign />} />
              <Route path="campaigns/:id" element={<NewCampaign />} />
              <Route path="campaigns/:id/edit" element={<NewCampaign />} />
              <Route path="segments" element={<Segments />} />
              <Route path="segments/new" element={<Segments />} />
              <Route path="segments/:id" element={<Segments />} />
              <Route path="segments/:id/edit" element={<Segments />} />
              <Route path="templates" element={<Templates />} />
              <Route path="templates/new" element={<Templates />} />
              <Route path="templates/:id" element={<Templates />} />
              <Route path="templates/:id/edit" element={<Templates />} />
            </Route>
            <Route path="onboarding">
              <Route path=":step" element={<OnboardingWizard />} />
              <Route index element={<OnboardingWizard />} />
            </Route>
            <Route path="patients" element={<RouteGuard feature="patient_management"><Patients /></RouteGuard>} />
            <Route path="appointments" element={<RouteGuard feature="patient_management"><Appointments /></RouteGuard>} />
            <Route path="inventory" element={<RouteGuard feature="inventory"><Inventory /></RouteGuard>} />
            <Route path="orders" element={<RouteGuard feature="orders"><Outlet /></RouteGuard>}>
              <Route index element={<OrdersList />} />
              <Route path="new" element={<NewOrder />} />
              <Route path=":id" element={<OrderDetail />} />
              <Route path=":id/invoice" element={<InvoiceView />} />
            </Route>
            <Route path="imports" element={<RouteGuard feature="imports"><ImportWizard /></RouteGuard>} />
            <Route path="loyalty" element={<RouteGuard feature="loyalty"><Outlet /></RouteGuard>}>
              <Route index element={<LoyaltyDashboard />} />
              <Route path="rewards" element={<LoyaltyRewards />} />
              <Route path="members" element={<LoyaltyMembers />} />
              <Route path="tiers" element={<LoyaltyTiers />} />
              <Route path="transactions" element={<LoyaltyTransactions />} />
            </Route>
            {/* /app/tests (Ophthalmology Tests) is unrouted: the page is mock-only. */}
            {/* training, hipaa-compliance, security, billing and admin/migration are unrouted: mock-only pages with no backend. */}
            <Route path="faq" element={<FAQ />} />
            <Route path="iris" element={<RouteGuard feature="iris"><IrisDashboard /></RouteGuard>} />
            <Route path="knowledge" element={<RouteGuard feature="knowledge"><Outlet /></RouteGuard>}>
              <Route index element={<KnowledgeBase />} />
              <Route path="new" element={<NoteEditor />} />
              <Route path="graph" element={<GraphView />} />
              <Route path=":noteId" element={<NoteViewer />} />
              <Route path=":noteId/edit" element={<NoteEditor />} />
            </Route>
            <Route path="settings" element={<RouteGuard feature="settings"><Settings /></RouteGuard>} />
            <Route path="chain">
              <Route path="corporation" element={<RouteGuard feature="chain.corporation"><CorporationList /></RouteGuard>} />
              <Route path="corporation/:id" element={<RouteGuard feature="chain.corporation"><CorporationDetail /></RouteGuard>} />
              <Route path="regions" element={<RouteGuard feature="chain.regions"><RegionsList /></RouteGuard>} />
              <Route path="regions/:id" element={<RouteGuard feature="chain.regions"><RegionDetail /></RouteGuard>} />
              <Route path="districts" element={<RouteGuard feature="chain.districts"><DistrictsList /></RouteGuard>} />
              <Route path="districts/:id" element={<RouteGuard feature="chain.districts"><DistrictDetail /></RouteGuard>} />
              <Route path="stores" element={<RouteGuard feature="chain.stores"><StoresList /></RouteGuard>} />
              <Route path="stores/:id" element={<RouteGuard feature="chain.stores"><StoreDetail /></RouteGuard>} />
              <Route path="practices" element={<RouteGuard feature="chain.practices"><PracticesList /></RouteGuard>} />
              <Route path="practices/:id" element={<RouteGuard feature="chain.practices"><PracticeDetail /></RouteGuard>} />
            </Route>
          </Route>

          {/* Corporate Admin Routes */}
          <Route path="/corporate/login" element={<CorporateLogin />} />
          <Route path="/corporate">
            <Route index element={<CorporateDashboard />} />
            <Route path="dashboard" element={<CorporateDashboard />} />
            {/* These routes will be implemented as needed */}
            {/* <Route path="corporations" element={<CorporationsList />} /> */}
            {/* <Route path="corporations/:id" element={<CorporationDetails />} /> */}
            {/* <Route path="practices" element={<PracticesList />} /> */}
            {/* <Route path="practices/:id" element={<PracticeDetails />} /> */}
            {/* <Route path="patients" element={<PatientsList />} /> */}
            {/* <Route path="admins" element={<AdminsList />} /> */}
            {/* <Route path="audit-logs" element={<AuditLogs />} /> */}
            {/* <Route path="analytics" element={<CorporateAnalytics />} /> */}
            {/* <Route path="settings" element={<CorporateSettings />} /> */}
            {/* <Route path="profile" element={<AdminProfile />} /> */}
          </Route>

          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
